/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get */
import Service, { service } from '@ember/service';
import { computed } from '@ember/object';
import { tracked } from '@glimmer/tracking';

export default class ControlPanelService extends Service {
  @service
  students;

  @service
  strings;

  @tracked
  controlPanelItems;

  init() {
    super.init(...arguments);
    this.get('students.all');

    // Initialize the items in init to avoid leaking state
    this.controlPanelItems = [
      {
        id: 'capture',
        icon: 'camera-plus',
        title: 'Capture Learning',
        description: 'Capture & Upload Evidence',
        mobileDescription: 'Capture & Upload Evidence',
        flexClass: 'flex-100',
        smFlexClass: 'flex-100',
        action: 'newPost',
        actionParams: [1],
        subPanelTitle: 'Capture Learning',
        subPanelDescription: 'Capture & Upload Evidence',
      },
      {
        id: 'plan',
        icon: 'strategy',
        title: 'Plan',
        descriptionTemplate: true, // Flag to indicate this needs template processing
        description: 'Whole school {{capitalize strings.topicLabel}}.',
        mobileDescription: 'Whole school {{capitalize strings.topicLabel}}.',
        flexClass: 'flex-33',
        smFlexClass: 'flex-sm-50 flex-xs-50',
        action: 'openSubPanel',
        actionParams: ['plan'],
        subPanelTitle: 'Plan',
        subPanelDescriptionTemplate: true,
        subPanelDescription: 'Whole school {{capitalize strings.topicLabel}}.',
      },
      {
        id: 'teach',
        icon: 'puzzle-piece',
        title: 'Teach',
        descriptionTemplate: true,
        description:
          'Start {{capitalize strings.topicLabel}}, communicate with families and assess learning.',
        mobileDescription: 'Start {{capitalize strings.topicLabel}}.',
        flexClass: 'flex-33',
        smFlexClass: 'flex-sm-50 flex-xs-50',
        action: 'openSubPanel',
        actionParams: ['teach', true],
        subPanelTitle: 'Teach',
        subPanelDescriptionTemplate: true,
        subPanelDescription:
          'Start {{capitalize strings.topicLabel}}, communicate with families and assess learning.',
        additionalDescription: 'Select a group below.',
      },
      {
        id: 'scores',
        icon: 'exam',
        title: 'Add Scores',
        description: 'Record assessment results.',
        mobileDescription: 'Record results.',
        flexClass: 'flex-33',
        smFlexClass: 'flex-sm-50 flex-xs-50',
        action: 'openSubPanel',
        actionParams: ['scores', true],
        subPanelTitle: 'Scores',
        subPanelDescription: 'Record assessment results.',
      },
      {
        id: 'review',
        icon: 'eye',
        title: 'Review content',
        description: 'Review learning artefacts by group.',
        mobileDescription: 'Review by group.',
        flexClass: 'flex-33',
        smFlexClass: 'flex-sm-50 flex-xs-50',
        action: 'openSubPanel',
        actionParams: ['posts', true],
        subPanelTitle: 'Review',
        subPanelDescription: 'Review learning artefacts by group.',
      },
      {
        id: 'triangulate',
        icon: 'triangle-dashed',
        title: 'Triangulate',
        description: 'Triangulate data to make overall judgements.',
        mobileDescription: 'Triangulate data.',
        flexClass: 'flex-33',
        smFlexClass: 'flex-sm-50 flex-xs-50',
        action: 'openSubPanel',
        actionParams: ['triangulate', true],
        subPanelTitle: 'Triangulate Data',
        subPanelDescription: 'Triangulate data to make overall judgements.',
      },
      {
        id: 'discuss',
        icon: 'chats-circle',
        title: 'Discuss',
        description:
          "Data Walls help centre discussions on student's and their learning.",
        mobileDescription: 'Data Walls.',
        flexClass: 'flex-33',
        smFlexClass: 'flex-sm-50 flex-xs-50',
        action: 'openSubPanel',
        actionParams: ['discuss', true],
        subPanelTitle: 'Discuss Learning',
        subPanelDescription:
          "Data Walls help centre discussions on student's and their learning.",
      },
      {
        id: 'evidence',
        icon: 'clipboard-text',
        title: 'Document',
        description: 'Gather evidence of excellence across the school.',
        mobileDescription: 'Gather evidence.',
        flexClass: 'flex-33',
        smFlexClass: 'flex-sm-50 flex-xs-50',
        action: 'openSubPanel',
        actionParams: ['evidence'],
        subPanelTitle: 'Document',
        subPanelDescription: 'Gather evidence of excellence across the school.',
      },
      {
        id: 'communicate',
        icon: 'megaphone',
        title: 'Communicate',
        description: 'Communicate with students, staff and families.',
        mobileDescription: 'Communicate with your community.',
        flexClass: 'flex-33',
        smFlexClass: 'flex-sm-50 flex-xs-50',
        action: 'openSubPanel',
        actionParams: ['communicate', true],
        subPanelTitle: 'Communicate',
        subPanelDescription: 'Communicate with students, staff and families.',
      },
      {
        id: 'read',
        icon: 'article',
        title: 'Read',
        description: 'Read messages in your feed.',
        mobileDescription: 'Read messages.',
        flexClass: 'flex-33',
        smFlexClass: 'flex-sm-50 flex-xs-50',
        action: 'goToRoute',
        actionParams: ['my-feed'],
        subPanelTitle: 'Read',
        subPanelDescription: 'Read messages in your feed.',
      },
      {
        id: 'guides',
        icon: 'compass',
        title: 'Guides',
        description:
          'Read Hero guides to extend your use and learn new tricks.',
        mobileDescription: 'Read Hero guides.',
        flexClass: 'flex-33',
        smFlexClass: 'flex-sm-50 flex-xs-50',
        action: 'goToRoute',
        actionParams: ['guides'],
        subPanelTitle: 'Guides',
        subPanelDescription:
          'Read Hero guides to extend your use and learn new tricks.',
      },
    ];
  }

  @computed('controlPanelItems.[]')
  get panelRows() {
    const items = this.controlPanelItems;
    const rows = [];

    // First item is alone in its own row
    rows.push([items[0]]);

    // Remaining items go two per row
    for (let i = 1; i < items.length; i += 2) {
      const row = [items[i]];
      if (items[i + 1]) {
        row.push(items[i + 1]);
      }
      rows.push(row);
    }

    return rows;
  }

  // Function to get item by linkType
  getItemByLinkType(linkType) {
    if (!linkType) return null;

    const items = this.controlPanelItems;
    return items.find(
      (item) => item.actionParams && item.actionParams[0] === linkType,
    );
  }

  // Computed property to get formatted descriptions
  @computed('controlPanelItems.[]', 'strings.topicLabel')
  get formattedDescriptions() {
    const items = this.controlPanelItems;
    const topicLabel = this.get('strings.topicLabel') || 'topic';

    return items.map((item) => {
      const formattedItem = { ...item };

      // Process description if it's a template
      if (item.descriptionTemplate) {
        formattedItem.processedDescription = item.description.replace(
          /{{capitalize strings.topicLabel}}/g,
          this._capitalize(topicLabel),
        );
        formattedItem.processedMobileDescription =
          item.mobileDescription.replace(
            /{{capitalize strings.topicLabel}}/g,
            this._capitalize(topicLabel),
          );
      } else {
        formattedItem.processedDescription = item.description;
        formattedItem.processedMobileDescription = item.mobileDescription;
      }

      // Process subPanel description if it's a template
      if (item.subPanelDescriptionTemplate) {
        formattedItem.processedSubPanelDescription =
          item.subPanelDescription.replace(
            /{{capitalize strings.topicLabel}}/g,
            this._capitalize(topicLabel),
          );
      } else {
        formattedItem.processedSubPanelDescription = item.subPanelDescription;
      }

      return formattedItem;
    });
  }

  // Helper method to capitalize a string
  _capitalize(str) {
    if (typeof str !== 'string' || !str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}
